import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useEffect } from 'react';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import StreamIQEntitlementService from '@jwp/ott-common/src/services/integrations/streamiq/StreamIQEntitlementService';

import useEventCallback from './useEventCallback';
import { useWatchHistory } from './useWatchHistory';

// @CUSTOMER vd-ott-iq This hook has been changed to start/stop playback sessions and update the watch history
export default function useProtectedMedia(item: PlaylistItem, streamingProtocol: 'dash' | 'hls' = 'dash', shouldKeepPreviousData = false) {
  const entitlementService = getModule(StreamIQEntitlementService);
  const watchHistory = useWatchHistory(item);

  const stopSession = useEventCallback(async () => {
    const watchHistoryItem = useWatchHistoryStore.getState().watchHistory.find((item) => item.mediaid === item.mediaid);
    const positionInMs = watchHistoryItem ? item.duration * watchHistoryItem.progress * 1000 : 0;

    await entitlementService.stopCurrentSession({ positionInMs });
  });

  useEffect(() => {
    return () => {
      void stopSession();
    };
  }, [stopSession]);

  const {
    data: { playableItem, mainContentOffset } = {},
    isLoading,
    error,
  } = useQuery({
    staleTime: 0,
    gcTime: 0,
    queryKey: ['media-signed', item.mediaid],
    queryFn: async () => {
      await stopSession();

      const session = await entitlementService.startSession({ item, streamingProtocol });

      return {
        mainContentOffset: (session?.playInfo.timeline?.find(({ type }) => type === 'main')?.positionInMs || 0) / 1000,
        playableItem: {
          ...item,
          sources: [
            {
              file: session.playInfo.url,
              type: streamingProtocol === 'dash' ? 'application/dash+xml' : 'application/vnd.apple.mpegurl',
            },
          ],
        },
      };
    },
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    retry: false,
  });

  return {
    playableItem,
    startOffset: watchHistory.startTime || mainContentOffset || 0,
    isLoading,
    error,
  };
}
// /CUSTOMER
